import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '@/config/supabase';
import { queryClient } from '@/config/queryClient';
import type { User } from '@/types';
import type { User as SupabaseUser } from '@supabase/supabase-js';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  errorCode: string | null;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName: string) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: User | null) => void;
  clearError: () => void;
  resetPassword: (email: string) => Promise<void>;
}

// Maps a normalized error code to user-friendly copy. `code` (not the message
// text) is the source of truth for any UI branching - see resolveAuthError.
const AUTH_ERROR_MESSAGES: Record<string, string> = {
  // Login errors
  'invalid_credentials': 'Invalid email or password. Please try again.',
  'invalid_grant': 'Invalid email or password. Please try again.',
  'user_not_found': 'No account found with this email address.',

  // Registration errors
  'user_already_exists': 'An account with this email already exists.',
  'email_exists': 'An account with this email already exists.',
  'weak_password': 'Password is too weak. Please use a stronger password.',
  'invalid_email': 'Please enter a valid email address.',
  'invalid_name': 'Display name must be at least 2 characters.',

  // Rate limiting
  'over_request_rate_limit': 'Too many attempts. Please wait a moment and try again.',
  'over_email_send_rate_limit': 'Too many emails sent. Please wait before requesting another.',

  // Email verification
  'email_not_confirmed': 'Please verify your email address before logging in.',
  'email_confirmation_required': 'Please check your email to verify your account.',

  // General errors
  'network_error': 'Network error. Please check your connection.',
  'server_error': 'Server error. Please try again later.',
};

// Resolves a thrown error (our own `{ code, message }` objects, or a raw
// Supabase AuthError) into a normalized code + friendly message in one pass,
// so the two can never drift apart the way a separate message-string lookup
// used to (UI components should branch on `code`, never on translated text).
function resolveAuthError(error: any): { code: string; message: string } {
  const rawMessage = (error?.message || '').toLowerCase();

  if (rawMessage.includes('invalid login credentials')) {
    return { code: 'invalid_credentials', message: AUTH_ERROR_MESSAGES.invalid_credentials! };
  }
  if (rawMessage.includes('user already registered')) {
    return { code: 'user_already_exists', message: AUTH_ERROR_MESSAGES.user_already_exists! };
  }
  if (rawMessage.includes('email not confirmed')) {
    return { code: 'email_not_confirmed', message: AUTH_ERROR_MESSAGES.email_not_confirmed! };
  }

  const code = error?.code || error?.message || 'unknown_error';
  const message = AUTH_ERROR_MESSAGES[code] || error?.message || 'An unexpected error occurred. Please try again.';
  return { code, message };
}

// Builds the app User from a Supabase auth user + (possibly missing) profiles
// row. Single source for this mapping so login, the auth listener, and OAuth
// can't drift apart.
export function buildUserFromProfile(authUser: SupabaseUser, profile: any): User {
  return {
    id: authUser.id,
    email: authUser.email!,
    displayName: profile?.display_name || authUser.user_metadata?.display_name || 'User',
    username: profile?.username || authUser.email?.split('@')[0] || 'user',
    avatar: profile?.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${authUser.id}`,
    bio: profile?.bio || null,
    isVerified: profile?.is_verified || false,
    level: Math.floor((profile?.impact_score || 0) / 100) + 1,
    city: profile?.city,
    country: profile?.country,

    // Stats
    decisionsCount: profile?.decisions_count || 0,
    votesCount: profile?.votes_count || 0,
    commentsCount: profile?.comments_count || 0,
    helpfulCount: profile?.helpful_count || 0,
    followersCount: profile?.followers_count || 0,
    followingCount: profile?.following_count || 0,

    // Gamification
    impactScore: profile?.impact_score || 0,
    currentStreak: profile?.current_streak || 0,
    longestStreak: profile?.longest_streak || 0,

    createdAt: authUser.created_at,
    profileCompletion: profile?.profile_completion || 0,
  };
}

async function fetchUser(authUser: SupabaseUser): Promise<User> {
  const { data: profile } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', authUser.id)
    .single();

  return buildUserFromProfile(authUser, profile);
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      errorCode: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null, errorCode: null });

        try {
          const { data, error } = await supabase.auth.signInWithPassword({
            email: email.trim().toLowerCase(),
            password,
          });

          if (error) throw error;
          if (!data.user) throw new Error('Login failed. Please try again.');

          const user = await fetchUser(data.user);
          set({ user, isAuthenticated: true, isLoading: false });
        } catch (error: any) {
          console.error('Login error:', error);
          const resolved = resolveAuthError(error);
          set({ error: resolved.message, errorCode: resolved.code, isLoading: false });
        }
      },

      register: async (email: string, password: string, displayName: string) => {
        set({ isLoading: true, error: null, errorCode: null });

        try {
          const normalizedEmail = email.trim().toLowerCase();

          const { data, error } = await supabase.auth.signUp({
            email: normalizedEmail,
            password,
            options: {
              data: { display_name: displayName.trim() },
            },
          });

          if (error) throw error;
          if (!data.user) throw new Error('Registration failed. Please try again.');

          // When the email is already registered (and confirmations are on),
          // Supabase returns a stub user with an empty identities array
          // instead of an error, to avoid revealing account existence. Detect
          // it so the user isn't told "verify your email" for an account
          // that already exists.
          if (data.user.identities && data.user.identities.length === 0) {
            set({ isLoading: false });
            throw { code: 'user_already_exists' };
          }

          // Check if email confirmation is required
          if (!data.session) {
            set({ isLoading: false });
            // Message comes from AUTH_ERROR_MESSAGES via resolveAuthError below -
            // keep this object code-only so there's one canonical copy of the text.
            throw { code: 'email_confirmation_required' };
          }

          // Note: Profile is auto-created by database trigger (handle_new_user),
          // which may not have run yet - buildUserFromProfile falls back to the
          // signup metadata for anything missing.
          const user = await fetchUser(data.user);
          set({ user, isAuthenticated: true, isLoading: false });
        } catch (error: any) {
          console.error('Registration error:', error);
          const resolved = resolveAuthError(error);
          set({ error: resolved.message, errorCode: resolved.code, isLoading: false });
        }
      },

      resetPassword: async (email: string) => {
        set({ isLoading: true, error: null, errorCode: null });

        try {
          const { error } = await supabase.auth.resetPasswordForEmail(
            email.trim().toLowerCase(),
            { redirectTo: `${window.location.origin}/reset-password` }
          );

          if (error) throw error;
          set({ isLoading: false });
        } catch (error: any) {
          console.error('Reset password error:', error);
          const resolved = resolveAuthError(error);
          set({ error: resolved.message, errorCode: resolved.code, isLoading: false });
        }
      },

      logout: async () => {
        set({ isLoading: true });
        try {
          await supabase.auth.signOut();
        } catch (error) {
          // Even if the server call fails, drop the local session so the UI
          // never stays "logged in" with a dead token.
          console.error('Logout error:', error);
        } finally {
          set({ user: null, isAuthenticated: false, error: null, errorCode: null, isLoading: false });
          // Drop cached feed/bookmarks/notifications so the next account on
          // this browser doesn't see this user's data.
          queryClient.clear();
        }
      },

      setUser: (user) => {
        set({ user, isAuthenticated: !!user });
      },

      clearError: () => {
        set({ error: null, errorCode: null });
      },
    }),
    {
      name: 'decixa-auth',
      partialize: (state) => ({ user: state.user, isAuthenticated: state.isAuthenticated }),
    }
  )
);

// ============================================
// AUTH STATE LISTENER
// ============================================
// Must be called once at app startup. Keeps the Zustand store in sync with
// the Supabase session lifecycle: expiry, sign-out (including other tabs),
// token refresh, and stale persisted state.
let authListenerStarted = false;
// True while the current session came from a password-recovery link. Such a
// session is temporary and single-purpose (ResetPasswordPage calls
// updateUser() with it) — it must never flip the app into "logged in", or
// AuthLayout would redirect away from /reset-password before the user can
// set a new password.
let isRecoveryFlow = false;

// Lets ResetPasswordPage distinguish a recovery-link session from a normal
// logged-in session (which must NOT unlock the reset form — that would
// bypass the current-password check on Change Password).
export function isPasswordRecoverySession(): boolean {
  return isRecoveryFlow;
}

export const initAuthListener = () => {
  if (authListenerStarted) return;
  authListenerStarted = true;

  // IMPORTANT: this callback must stay synchronous. supabase-js holds an
  // internal auth lock while dispatching these events; any Supabase call in
  // here (a query, getSession, ...) waits on that same lock and deadlocks
  // every future getSession() in the app — which froze all data fetching
  // (e.g. the home feed) right after an OAuth sign-in. Async work is
  // deferred with setTimeout so it runs after the lock is released.
  supabase.auth.onAuthStateChange((event, session) => {
    if (event === 'PASSWORD_RECOVERY') {
      isRecoveryFlow = true;
      return;
    }

    if (event === 'SIGNED_OUT') {
      isRecoveryFlow = false;
      const wasAuthenticated = useAuthStore.getState().isAuthenticated;
      useAuthStore.setState({ user: null, isAuthenticated: false, isLoading: false });
      // Covers sign-outs that don't go through logout(), e.g. another tab.
      if (wasAuthenticated) queryClient.clear();
      return;
    }

    if (!session) {
      // INITIAL_SESSION with no session: localStorage said "logged in" but
      // Supabase has no session (expired/cleared) — drop the stale state.
      if (event === 'INITIAL_SESSION' && useAuthStore.getState().isAuthenticated) {
        useAuthStore.setState({ user: null, isAuthenticated: false, isLoading: false });
      }
      return;
    }

    // On INITIAL_SESSION Zustand persist already holds the user — skip the DB
    // fetch to avoid competing with EditProfilePage's own loadProfile() query.
    // SIGNED_IN and TOKEN_REFRESHED still fetch to get fresh profile data.
    if (event === 'INITIAL_SESSION' && useAuthStore.getState().user) return;

    if (!session.user) return;

    // A recovery-link session also arrives as SIGNED_IN/INITIAL_SESSION —
    // never auto-authenticate from it. The pathname check covers the case
    // where INITIAL_SESSION fires before PASSWORD_RECOVERY sets the flag.
    if (isRecoveryFlow || window.location.pathname.startsWith('/reset-password')) return;

    const sessionUser = session.user;
    setTimeout(async () => {
      try {
        const user = await fetchUser(sessionUser);

        // Guard against race: if the user logged out while this fetch was
        // in-flight, don't re-authenticate them. (OAuth sign-in is completed
        // explicitly by OAuthSigninPage — its SIGNED_IN fires while the store
        // is still logged out, and that page sets the user itself.)
        if (event !== 'INITIAL_SESSION' && !useAuthStore.getState().isAuthenticated) return;

        useAuthStore.getState().setUser(user);
      } catch (error) {
        console.error('Auth listener profile fetch failed:', error);
      }
    }, 0);
  });
};
